import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, map } from 'rxjs/operators';
import { AlertsService } from 'app/modules/alerts/alerts.service';

/**
 * Shared cache service for alerts to prevent duplicate HTTP calls
 * Multiple components can subscribe to the same data without triggering new requests
 */
@Injectable({ providedIn: 'root' })
export class SharedAlertsCacheService {
    
    private cachedAlerts$: Observable<any> | null = null;
    private cacheTimestamp: number = 0;
    private readonly CACHE_DURATION_MS = 2 * 60 * 1000; // 2 minutes

    constructor(private alertsService: AlertsService) {}

    /**
     * Get alerts with automatic caching and sharing
     * All subscribers get the same data without duplicate HTTP calls
     */
    getSharedAlerts(): Observable<any> {
        const now = Date.now();
        
        // Reset cache if expired
        if (this.cachedAlerts$ && (now - this.cacheTimestamp) > this.CACHE_DURATION_MS) {
            this.cachedAlerts$ = null;
        }

        // Create new cached observable if needed
        if (!this.cachedAlerts$) {
            this.cacheTimestamp = now;
            this.cachedAlerts$ = this.alertsService.getMyAlerts({
                severity: 'critical,high' as any,
                status: 'new',
                limit: 15, // Get 15 to satisfy all consumers
                sort: 'created_at',
                order: 'desc'
            }).pipe(
                // Share the result among all subscribers
                shareReplay({ 
                    bufferSize: 1, 
                    refCount: true // Auto-cleanup when no subscribers
                })
            );
        }

        return this.cachedAlerts$;
    }

    /**
     * Get only critical alerts (filtered from shared cache)
     */
    getCriticalAlerts(limit: number = 15): Observable<any> {
        return this.getSharedAlerts().pipe(
            map(response => ({
                ...response,
                data: response.data
                    .filter(alert => alert.severity === 'critical')
                    .slice(0, limit),
                pagination: {
                    ...response.pagination,
                    per_page: limit,
                    to: Math.min(limit, response.data.filter(a => a.severity === 'critical').length)
                }
            }))
        );
    }

    /**
     * Get critical + high alerts (from shared cache, limited)
     */
    getCriticalAndHighAlerts(limit: number = 10): Observable<any> {
        return this.getSharedAlerts().pipe(
            map(response => ({
                ...response,
                data: response.data.slice(0, limit),
                pagination: {
                    ...response.pagination,
                    per_page: limit,
                    to: Math.min(limit, response.data.length)
                }
            }))
        );
    }

    /**
     * Force refresh the cache
     */
    refresh(): void {
        this.cachedAlerts$ = null;
        this.cacheTimestamp = 0;
    }
}
